/**
 * Constants for Australian states
 */
const STATE_CONFIG = {
  CENTERS: {
    'VIC': [144.9631, -37.8136],
    'QLD': [153.0251, -27.4698],
    'NSW': [151.2093, -33.8688],
    'WA': [115.8605, -31.9505],
    'TAS': [147.3272, -42.8821],
    'SA': [138.6007, -34.9285],
    'ACT': [149.1300, -35.2809]
  },
  ZOOMS: {
    'VIC': 7, 'QLD': 6, 'NSW': 7, 'WA': 6,
    'TAS': 7, 'SA': 6, 'ACT': 10
  },
  NAMES: {
    'VIC': 'Victoria',
    'QLD': 'Queensland',
    'NSW': 'New South Wales',
    'WA': 'Western Australia',
    'TAS': 'Tasmania',
    'SA': 'South Australia',
    'ACT': 'Australian Capital Territory'
  }
};

/**
 * Map configuration constants
 */
const MAP_CONSTANTS = {
  DEFAULT_ZOOM: 12,
  LOCATION_ZOOM: 14,
  MAX_ZOOM: 14,
  COORDINATE_TOLERANCE: 0.0001,
  GEOLOCATION_TIMEOUT: 10000,
  MAP_CHECK_INTERVAL: 100
};

class FranchiseOpportunities {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.sectionElement = document.querySelector(`[data-section-id="${sectionId}"]`);
    this.mapElement = document.getElementById(`franchise-map-${sectionId}`);
    
    if (!this.mapElement) {
      return;
    }
    
    const dataScript = document.getElementById(`franchise-locations-data-${sectionId}`);
    if (!dataScript) {
      console.error('Franchise locations data not found');
      return;
    }
    
    const data = JSON.parse(dataScript.textContent);
    this.googleApiKey = this.mapElement.dataset.googleApiKey;
    this.defaultState = data.defaultState;
    this.enableGeolocation = this.mapElement.dataset.enableGeolocation === 'true';
    this.clusterThreshold = parseInt(this.mapElement.dataset.clusterThreshold) || 10;
    this.enableClustering = this.mapElement.dataset.enableMapClustering === 'true';
    this.mapId = 'franchise_opportunities_google_map';
    this.locationsData = data.locationsByState;
    
    this.currentState = this.defaultState;
    this.map = null;
    this.markers = [];
    this.currentInfoWindow = null;
    this.markerCluster = null;
    this.locations = this.parseLocationsData();
    this.markerIconUrl = null;
    
    this.init();
  }

  parseLocationsData() {
    if (!this.locationsData) {
      console.error('No locationsData available');
      return {};
    }
    
    const locationsByState = {};
    Object.keys(this.locationsData).forEach(state => {
      const locations = this.locationsData[state];
      if (locations && Array.isArray(locations)) {
        locationsByState[state] = [...locations].sort((a, b) => b.priority - a.priority);
      }
    });
    return locationsByState;
  }

  async init() {
    await this.initMap();
    this.bindEvents();
    this.setCurrentState(this.defaultState);
    this.detectUserLocation();
  }

  async detectUserLocation() {
    if (!this.enableGeolocation || !navigator.geolocation) return;
    
    try {
      const position = await this.getCurrentPosition();
      const state = await this.reverseGeocode(position.coords);
      if (state) this.setCurrentState(state);
    } catch (error) {
      console.log('Geolocation failed, using default state:', this.defaultState);
    }
  }

  getMarkerLatLng(marker) {
    if (marker?.getPosition) {
      const p = marker.getPosition();
      return { lat: p.lat(), lng: p.lng() };
    }
    if (marker?.position && typeof marker.position.lat === 'number') {
      return { lat: marker.position.lat, lng: marker.position.lng };
    }
    return null;
  }

  getCurrentPosition() {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error('Geolocation timeout')), MAP_CONSTANTS.GEOLOCATION_TIMEOUT);
      navigator.geolocation.getCurrentPosition(
        (position) => { clearTimeout(timeout); resolve(position); },
        (error) => { clearTimeout(timeout); reject(error); },
        { timeout: MAP_CONSTANTS.GEOLOCATION_TIMEOUT, enableHighAccuracy: false }
      );
    });
  }

  async reverseGeocode(coords) {
    return this.googleReverseGeocode(coords);
  }

  async googleReverseGeocode(coords) {
    try {
      const lat = coords.lat || coords.latitude;
      const lng = coords.lng || coords.longitude;
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${this.googleApiKey}`
      );
      const data = await response.json();
      
      if (data.results?.length > 0) {
        const stateComponent = data.results[0].address_components.find(component => 
          component.types.includes('administrative_area_level_1')
        );
        if (stateComponent) return this.normalizeStateName(stateComponent.short_name);
      }
    } catch (error) {
      console.error('Google reverse geocoding failed:', error);
    }
    return null;
  }


  /**
   * Normalizes state name/code to standard abbreviation
   * @param {string} stateCode - State code or full name
   * @returns {string|null} Normalized state code or null if not found
   */
  normalizeStateName(stateCode) {
    const availableStates = Object.keys(this.locations);
    if (availableStates.includes(stateCode)) return stateCode;
    
    // Create reverse lookup from STATE_CONFIG.NAMES
    const stateMap = {};
    for (const [code, name] of Object.entries(STATE_CONFIG.NAMES)) {
      stateMap[code] = code;
      stateMap[name] = code;
    }
    
    const normalizedState = stateMap[stateCode];
    return availableStates.includes(normalizedState) ? normalizedState : null;
  }

  async initMap() {
    if (!this.mapElement) return;
    await this.initGoogleMap();
  }

  async initGoogleMap() {
    if (!this.googleApiKey) {
      this.showMapError('Google Maps API key not configured');
      return;
    }

    if (typeof google === 'undefined' || !google.maps) {
      this.showMapError('Google Maps API not loaded. Check your API key and network connection.');
      return;
    }

    try {
      const { Map } = await google.maps.importLibrary("maps");
      const { AdvancedMarkerElement } = await google.maps.importLibrary("marker");

      const center = this.getStateCenter(this.currentState);
      const mapConfig = this.createMapConfig(center);
      
      this.map = new Map(this.mapElement, mapConfig);
      this.AdvancedMarkerElement = AdvancedMarkerElement;

      this.addMapClickListener();
    } catch (error) {
      this.showMapError(`Google Maps Error: ${error.message}`);
    }
  }

  createMapConfig(center) {
    const mapConfig = {
      zoom: MAP_CONSTANTS.DEFAULT_ZOOM,
      center: { lat: center[1], lng: center[0] },
      mapTypeControlOptions: { mapTypeIds: ["roadmap", "satellite", "hybrid", "terrain"] },
      zoomControlOptions: { position: google.maps.ControlPosition.LEFT_TOP },
      addressControl: false,
      linksControl: false,
      panControl: false,
      streetViewControl: false,
      zoomControl: true,
      draggable: true,
      mapTypeControl: false,
      disableDoubleClickZoom: true,
      fullscreenControl: false,
      scrollwheel: false
    };

    if (this.mapId && this.mapId !== 'DEMO_MAP_ID') {
      mapConfig.mapId = this.mapId;
    }
    return mapConfig;
  }

  addMapClickListener() {
    google.maps.event.addListener(this.map, "click", () => {
      if (this.currentInfoWindow) this.currentInfoWindow.close();
    });
  }

  showMapError(message) {
    if (this.mapElement) {
      this.mapElement.innerHTML = `
        <div style="padding: 20px; text-align: center; color: #666; background: #f5f5f5; border-radius: 4px;">
          <h3 style="margin: 0 0 10px 0; color: #d32f2f;">Map Loading Error</h3>
          <p style="margin: 0 0 10px 0;">${message}</p>
          <p style="margin: 0; font-size: 12px; color: #999;">Please check your Google Maps API key configuration in theme settings.</p>
        </div>
      `;
    }
  }


  addGoogleMarkers() {
    if (!this.map) return;
    
    const filteredLocations = this.getFilteredLocations();
    this.clearMarkers();
    
    const bounds = new google.maps.LatLngBounds();
    
    filteredLocations.forEach(location => {
      const coordinates = this.validateCoordinates(location);
      if (!coordinates) return;
      
      bounds.extend({ lat: coordinates.lat, lng: coordinates.lng });
      this.createMarker(location, coordinates.lat, coordinates.lng);
    });
    
    if (this.markers.length > 0) {
      if (this.enableClustering) this.createMarkerCluster();
      this.fitMapToBounds(bounds);
    }
  }

  validateCoordinates(location) {
    if (!location.coordinates?.lat || !location.coordinates?.lng) return null;
    
    const lat = parseFloat(location.coordinates.lat);
    const lng = parseFloat(location.coordinates.lng);
    
    if (isNaN(lat) || isNaN(lng)) return null;
    return { lat, lng };
  }

  createMarker(location, lat, lng) {
    try {
      if (this.AdvancedMarkerElement && this.map.mapId) {
        this.createAdvancedMarker(location, lat, lng);
      } else {
        this.createRegularMarker(location, lat, lng);
      }
    } catch (error) {
      this.createRegularMarker(location, lat, lng);
    }
  }

  fitMapToBounds(bounds) {
    if (bounds.isEmpty()) {
      const defaultCenter = STATE_CONFIG.CENTERS['VIC'];
      this.map.setCenter(new google.maps.LatLng(defaultCenter[1], defaultCenter[0]));
      this.map.setZoom(MAP_CONSTANTS.DEFAULT_ZOOM);
    } else {
      this.map.fitBounds(bounds);
      if (this.map.getZoom() > MAP_CONSTANTS.MAX_ZOOM) {
        this.map.setZoom(MAP_CONSTANTS.MAX_ZOOM);
      }
    }
  }

  createAdvancedMarker(location, lat, lng) {
    const markerContent = this.createMarkerContent();
    const marker = new this.AdvancedMarkerElement({
      position: { lat: lat, lng: lng },
      map: this.map,
      title: location.name,
      content: markerContent
    });

    this.addMarkerClickListener(marker, location);
    this.markers.push(marker);
  }

  /**
   * Gets and caches the marker icon URL from CSS custom property
   * @returns {string} Marker icon URL
   */
  getMarkerIconUrl() {
    if (!this.markerIconUrl) {
      this.markerIconUrl = getComputedStyle(this.sectionElement)
        .getPropertyValue('--google-marker-icon')
        .trim();
    }
    return this.markerIconUrl;
  }

  createMarkerContent() {
    const markerIconUrl = this.getMarkerIconUrl();
    const markerContent = document.createElement('div');
    
    if (markerIconUrl) {
      markerContent.innerHTML = `
        <div style="width: 56px; height: 65px; background-image: url('${markerIconUrl}'); background-size: contain; background-repeat: no-repeat; background-position: center;"></div>
      `;
    } else {
      markerContent.innerHTML = `
        <div style="width: 30px; height: 30px; background-color: #ffffff; border: 2px solid #ffffff; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: 12px; font-weight: bold;">
          📍
        </div>
      `;
    }
    
    return markerContent;
  }

  createRegularMarker(location, lat, lng) {
    const markerIconUrl = this.getMarkerIconUrl();
    
    const marker = new google.maps.Marker({
      map: this.map,
      position: { lat: lat, lng: lng },
      title: location.name,
      icon: {
        url: markerIconUrl,
        size: new google.maps.Size(56,65),
        scaledSize: new google.maps.Size(56,65),
        origin: new google.maps.Point(0,0),
        anchor: new google.maps.Point(28,65)
      }
    });

    this.addMarkerClickListener(marker, location);
    this.markers.push(marker);
  }

  addMarkerClickListener(marker, location) {
    const infoWindow = new google.maps.InfoWindow({
      content: this.createPopupContent(location),
      ariaLabel: location.name
    });

    marker.addListener('click', () => {
      if (this.currentInfoWindow) this.currentInfoWindow.close();
      this.currentInfoWindow = infoWindow;
      infoWindow.open(this.map, marker);
      this.attachInfoWindowEvents(infoWindow);
      this.map.setZoom(MAP_CONSTANTS.LOCATION_ZOOM);
      const pos = this.getMarkerLatLng(marker);
      if (pos) this.map.panTo(pos);
    });
  }

  getFilteredLocations() {
    return this.locations[this.currentState] || [];
  }
  
  findLocationById(locationId) {
    for (const state in this.locations) {
      const location = this.locations[state]?.find(loc => loc.id === locationId);
      if (location) return location;
    }
    return null;
  }

  findStateForLocation(locationId) {
    for (const state in this.locations) {
      const location = this.locations[state]?.find(loc => loc.id === locationId);
      if (location) return state;
    }
    return null;
  }

  getStateCenter(state) {
    if (STATE_CONFIG.CENTERS[state]) return STATE_CONFIG.CENTERS[state];
    
    const locations = this.locations[state];
    if (locations?.length > 0) return this.calculateCenterFromLocations(locations);
    return STATE_CONFIG.CENTERS['VIC'];
  }

  calculateCenterFromLocations(locations) {
    const coords = locations.map(loc => loc.coordinates);
    if (coords.length === 0) return STATE_CONFIG.CENTERS['VIC'];
    
    const avgLng = coords.reduce((sum, coord) => sum + coord.lng, 0) / coords.length;
    const avgLat = coords.reduce((sum, coord) => sum + coord.lat, 0) / coords.length;
    return [avgLng, avgLat];
  }

  getStateZoom(state) {
    return STATE_CONFIG.ZOOMS[state] || 5;
  }

  createPopupContent(location) {
    return `
      <div class="location-popup-info">
        <div class="location-popup-info__type-wrapper"><div class="location-popup-info__type">${location.type}</div></div>
        <div class="location-popup-info__name">${location.name}, ${location.state}</div>
        <a href="javascript:void(0)" class="location-popup-info__enquire-btn button button-primary" data-location-id="${location.id}">Enquire now</a>
      </div>
    `;
  }

  setCurrentState(state) {
    this.currentState = state;
    this.updateStateSelector();
    this.updateMapView();
    this.updateLocationList();
  }

  updateStateSelector() {
    this.sectionElement.querySelectorAll('.state-tab').forEach(tab => {
      tab.classList.toggle('active', tab.dataset.state === this.currentState);
    });
  }

  updateMapView() {
    if (!this.map) return;
    
    const center = this.getStateCenter(this.currentState);
    const zoom = this.getStateZoom(this.currentState);
    
    this.map.setCenter({ lat: center[1], lng: center[0] });
    this.map.setZoom(zoom);
    
    this.updateMarkers();
  }

  updateMarkers() {
    if (!this.map) return;
    this.addGoogleMarkers();
  }

  updateLocationList() {
    const stateGroups = this.sectionElement.querySelectorAll('.state-group');
    const noOpportunities = this.sectionElement.querySelector('.no-opportunities');
    const locationCount = this.sectionElement.querySelector('.location-count .count-number');
    
    stateGroups.forEach(group => {
      group.classList.remove('active');
      group.classList.add('hidden');
    });
    
    const currentStateGroup = document.querySelector(`.state-group[data-state="${this.currentState}"]`);
    if (currentStateGroup) {
      currentStateGroup.classList.remove('hidden');
      currentStateGroup.classList.add('active');
      
      const locationItems = currentStateGroup.querySelectorAll('.location-item');
      const count = locationItems.length;
      
      if (locationCount) locationCount.textContent = count;
      if (noOpportunities) {
        if (count === 0) {
          noOpportunities.classList.remove('hidden');
        } else {
          noOpportunities.classList.add('hidden');
        }
      }
    } else {
      if (locationCount) locationCount.textContent = '0';
      if (noOpportunities) noOpportunities.classList.remove('hidden');
    }
  }


  bindEvents() {
    this.sectionElement.querySelectorAll('.state-tab').forEach(element => {
      element.addEventListener('click', (e) => {
        if (e.target.classList.contains('state-tab')) {
          this.setCurrentState(e.target.dataset.state);
        }
      });
    });

    this.sectionElement.querySelectorAll('.franchise-opportunities__mobile-view-toggle .view-toggle').forEach(toggle => {
      toggle.addEventListener('click', (e) => {
        const view = e.target.dataset.view;
        this.toggleMobileView(view);
      });
    });

    this.sectionElement.addEventListener('click', (e) => {
      const locationItem = e.target.closest('.location-item');
      if (locationItem) {
        const locationId = locationItem.dataset.locationId;
        if (locationId) {
          this.focusOnLocation(locationId);
        } else {
          console.warn('No location ID found on clicked item');
        }
      }
      
      if (e.target.classList.contains('btn-enquire-form') || e.target.classList.contains('location-popup-info__enquire-btn')) {
        this.handleEnquireClick();
      }
    });

    this.sectionElement.addEventListener('franchise:state-changed', (e) => {
      if (e.detail?.state) this.setCurrentState(e.detail.state);
    });
  }

  toggleMobileView(view) {
    const leftColumn = document.querySelector('.franchise-opportunities__left-column');
    const rightColumn = document.querySelector('.franchise-opportunities__right-column');
    const toggles = document.querySelectorAll('.franchise-opportunities__mobile-view-toggle .view-toggle');
    
    toggles.forEach(toggle => {
      toggle.classList.toggle('active', toggle.dataset.view === view);
    });
    
    if (view === 'list') {
      if (leftColumn) leftColumn.classList.add('active');
      if (rightColumn) rightColumn.classList.remove('active');
    } else {
      if (leftColumn) leftColumn.classList.remove('active');
      if (rightColumn) rightColumn.classList.add('active');
    }
  }

  focusOnLocation(locationId) {
    if (!this.map) return;
    
    let location = this.findLocationById(locationId);
    if (!location) return;
    
    const locationState = this.findStateForLocation(locationId);
    
    if (locationState && locationState !== this.currentState) {
      this.setCurrentState(locationState);
    }
    
    const coordinates = this.validateCoordinates(location);
    if (!coordinates) return;
    
    this.map.setCenter({ lat: coordinates.lat, lng: coordinates.lng });
    this.map.setZoom(MAP_CONSTANTS.LOCATION_ZOOM);
    
    const marker = this.findMarkerByCoordinates(coordinates.lat, coordinates.lng);
    
    if (marker) {
      this.openInfoWindow(marker, location);
    } else {
      this.createTemporaryInfoWindow(location, coordinates.lat, coordinates.lng);
    }
  }

  findMarkerByCoordinates(lat, lng) {
    return this.markers.find(m => {
      const pos = this.getMarkerLatLng(m);
      return pos &&
        Math.abs(pos.lat - lat) < MAP_CONSTANTS.COORDINATE_TOLERANCE &&
        Math.abs(pos.lng - lng) < MAP_CONSTANTS.COORDINATE_TOLERANCE;
    });
  }

  /**
   * Handles enquire button click and scrolls to the target form section
   * Accounts for sticky header by adding offset equal to 2x header height
   */
  handleEnquireClick() {
    const target = this.sectionElement.dataset.formTarget;
    if (!target) return;
    
    const targetElement = document.querySelector(target);
    if (!targetElement) return;
    
    const sectionElement = targetElement.closest('.shopify-section');
    if (!sectionElement) return;
    
    const headerElement = document.querySelector('#header-component');
    const headerHeight = headerElement ? headerElement.offsetHeight : 0;
    const offset = headerHeight * 2;
    
    const elementPosition = sectionElement.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - offset;
    
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }
  
  openInfoWindow(marker, location) {
    this.closeCurrentInfoWindow();
    
    const infoWindow = new google.maps.InfoWindow({
      content: this.createPopupContent(location),
      ariaLabel: location.name
    });
    
    infoWindow.open(this.map, marker);
    this.currentInfoWindow = infoWindow;
    this.attachInfoWindowEvents(infoWindow);
  }
  
  createTemporaryInfoWindow(location, lat, lng) {
    this.closeCurrentInfoWindow();
    
    const infoWindow = new google.maps.InfoWindow({
      content: this.createPopupContent(location),
      ariaLabel: location.name
    });
    
    infoWindow.open(this.map, { lat: lat, lng: lng });
    this.currentInfoWindow = infoWindow;
    this.attachInfoWindowEvents(infoWindow);
  }

  /**
   * Converts InfoWindow content to a queryable element
   * @param {string|Element} content - InfoWindow content (string or element)
   * @returns {Element|null} DOM element or null
   */
  getInfoWindowElement(content) {
    if (!content) return null;
    
    // If content is already an Element, return it
    if (content instanceof Element) {
      return content;
    }
    
    // If content is a string, parse it into an element
    if (typeof content === 'string') {
      const wrapper = document.createElement('div');
      wrapper.innerHTML = content;
      return wrapper.firstElementChild;
    }
    
    return null;
  }

  /**
   * Attaches event listeners to info window content after it's rendered
   * Uses Google Maps domready event to ensure content is in the DOM
   * Automatically cleans up listeners when info window closes
   * Handles both string and element content types
   * @param {google.maps.InfoWindow} infoWindow - The info window instance
   */
  attachInfoWindowEvents(infoWindow) {
    let enquireClickHandler = null;
    
    google.maps.event.addListenerOnce(infoWindow, 'domready', () => {
      const rawContent = infoWindow.getContent();
      const content = this.getInfoWindowElement(rawContent);
      if (!content) return;
      
      const enquireBtn = content.querySelector('.location-popup-info__enquire-btn');
      if (enquireBtn) {
        enquireClickHandler = () => this.handleEnquireClick();
        enquireBtn.addEventListener('click', enquireClickHandler);
      }
    });
    
    google.maps.event.addListenerOnce(infoWindow, 'closeclick', () => {
      if (enquireClickHandler) {
        const rawContent = infoWindow.getContent();
        const content = this.getInfoWindowElement(rawContent);
        if (content) {
          const enquireBtn = content.querySelector('.location-popup-info__enquire-btn');
          if (enquireBtn) {
            enquireBtn.removeEventListener('click', enquireClickHandler);
          }
        }
        enquireClickHandler = null;
      }
    });
  }

  closeCurrentInfoWindow() {
    if (this.currentInfoWindow) this.currentInfoWindow.close();
  }

  clearMarkers() {
    if (this.markerCluster) {
      this.markerCluster.clearMarkers();
      this.markerCluster = null;
    }
    
    if (this.markers?.length > 0) {
      this.markers.forEach(marker => {
        if (marker?.setMap) marker.setMap(null);
      });
      this.markers = [];
    }
    
    this.closeCurrentInfoWindow();
    this.currentInfoWindow = null;
  }

  createMarkerCluster() {
    if (!this.markers?.length || !this.enableClustering) return;
    
    if (typeof markerClusterer !== 'undefined') {
      try {
        const renderer = this.createClusterRenderer();
        this.markerCluster = new markerClusterer.MarkerClusterer({ 
          markers: this.markers, 
          map: this.map,
          renderer: renderer,
          algorithm: new markerClusterer.GridAlgorithm({
            maxZoom: this.clusterThreshold
          })
        });
      } catch (error) {
        console.error('Error creating MarkerClusterer:', error);
      }
    }
  }

  createClusterRenderer() {
    return {
      render: ({count, position}) => {
        const svg = window.btoa(`
          <svg fill="#862633" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">
            <circle cx="120" cy="120" opacity="1" r="100" />
          </svg>
        `);
        return new google.maps.Marker({
          icon: {
            url: `data:image/svg+xml;base64,${svg}`,
            scaledSize: new google.maps.Size(54,54)
          },
          label: {
            text: String(count),
            color: "#fff",
            fontSize: "22px",
            fontFamily: "Arial, Colby Condsensed"
          },
          position,
          zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count
        });
      }
    };
  }

  /**
   * Cleanup method to remove all event listeners and map resources
   * Call this when the section is removed from DOM to prevent memory leaks
   */
  destroy() {
    // Clear all markers and clusters
    this.clearMarkers();
    
    // Close any open info windows
    if (this.currentInfoWindow) {
      this.currentInfoWindow.close();
      this.currentInfoWindow = null;
    }
    
    // Clear map instance
    if (this.map) {
      google.maps.event.clearInstanceListeners(this.map);
      this.map = null;
    }
    
    // Clear references
    this.markers = [];
    this.locations = {};
    this.AdvancedMarkerElement = null;
  }
}

/**
 * Global instance registry to prevent duplicate initializations
 * @type {Map<string, FranchiseOpportunities>}
 */
const franchiseInstances = new Map();

/**
 * Initialize franchise opportunities sections
 * Exposed globally for Liquid template callback after loading markerclusterer
 */
window.FranchiseOpportunities = {
  /**
   * Initialize all franchise opportunity sections
   * Deduplicates instances automatically
   */
  init: function() {
    if (typeof google === 'undefined' || !google.maps || !google.maps.importLibrary) {
      console.warn('Google Maps API not ready yet');
      return;
    }

    const sections = document.querySelectorAll('.franchise-opportunities-section');
    sections.forEach(section => {
      const sectionId = section.dataset.sectionId;
      
      // Deduplicate: only create instance if not already initialized
      if (!franchiseInstances.has(sectionId)) {
        const instance = new FranchiseOpportunities(sectionId);
        franchiseInstances.set(sectionId, instance);
      }
    });
  },

  /**
   * Destroy a specific instance or all instances
   * @param {string} [sectionId] - Optional section ID to destroy specific instance
   */
  destroy: function(sectionId) {
    if (sectionId) {
      const instance = franchiseInstances.get(sectionId);
      if (instance) {
        instance.destroy();
        franchiseInstances.delete(sectionId);
      }
    } else {
      // Destroy all instances
      franchiseInstances.forEach(instance => instance.destroy());
      franchiseInstances.clear();
    }
  }
};

document.addEventListener('DOMContentLoaded', function() {
  const checkGoogleMaps = () => {
    if (typeof google !== 'undefined' && google.maps && google.maps.importLibrary) {
      window.FranchiseOpportunities.init();
    } else {
      setTimeout(checkGoogleMaps, MAP_CONSTANTS.MAP_CHECK_INTERVAL);
    }
  };
  checkGoogleMaps();
});

/**
 * Shared helper to create state name to code mapping from STATE_CONFIG
 * @returns {Object} State name to code mapping
 */
function getStateNameMapping() {
  const mapping = {};
  for (const [code, name] of Object.entries(STATE_CONFIG.NAMES)) {
    mapping[name] = code;
  }
  return mapping;
}

// Franchise Opportunities UI Controller
class FranchiseOpportunitiesUI {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.sectionElement = null;
    this.eventHandlers = [];
    this.resizeHandler = null;
    this.init();
  }

  /**
   * Shows/hides state groups based on selected state
   * @param {string} selectedState - State code to show
   * @param {NodeList} stateGroups - List of state group elements
   */
  showStateGroup(selectedState, stateGroups) {
    stateGroups.forEach((group) => {
      if (!group) return;
      const groupState = group.getAttribute('data-state');
      const isActive = groupState === selectedState;
      group.classList.toggle('hidden', !isActive);
      group.classList.toggle('active', isActive);
    });
  }

  /**
   * Sets the active state across desktop and mobile UI
   * @param {string} stateCode - State code to activate
   * @param {NodeList} statePills - Desktop state tabs
   * @param {NodeList} stateGroups - Desktop state groups
   * @param {NodeList|null} mobileStatePills - Mobile state tabs
   * @param {NodeList|null} mobileStateGroups - Mobile state groups
   * @returns {boolean} Success status
   */
  setActiveState(stateCode, statePills, stateGroups, mobileStatePills = null, mobileStateGroups = null) {
    const targetPill = Array.from(statePills).find(pill => pill.getAttribute('data-state') === stateCode);
    const targetGroup = Array.from(stateGroups).find(group => group.getAttribute('data-state') === stateCode);
    
    if (!targetPill || !targetGroup) return false;

    // Deactivate all pills and groups
    const deactivateElements = (elements) => {
      elements.forEach(el => {
        if (el) {
          el.classList.remove('active');
          if (el.classList.contains('state-group')) {
            el.classList.add('hidden');
          }
        }
      });
    };

    deactivateElements(statePills);
    deactivateElements(stateGroups);
    if (mobileStatePills) deactivateElements(mobileStatePills);
    if (mobileStateGroups) deactivateElements(mobileStateGroups);
    
    // Activate target elements
    targetPill.classList.add('active');
    targetGroup.classList.add('active');
    targetGroup.classList.remove('hidden');
    
    // Activate mobile targets if available
    if (mobileStatePills) {
      const mobileTargetPill = Array.from(mobileStatePills).find(pill => pill.getAttribute('data-state') === stateCode);
      if (mobileTargetPill) mobileTargetPill.classList.add('active');
    }
    if (mobileStateGroups) {
      const mobileTargetGroup = Array.from(mobileStateGroups).find(group => group.getAttribute('data-state') === stateCode);
      if (mobileTargetGroup) {
        mobileTargetGroup.classList.add('active');
        mobileTargetGroup.classList.remove('hidden');
      }
    }
    
    // Dispatch state change event for map integration
    const mapUpdateEvent = new CustomEvent('franchise:state-changed', {
      detail: { state: stateCode },
      bubbles: true
    });
    this.sectionElement.dispatchEvent(mapUpdateEvent);
    return true;
  }

  /**
   * Reverse geocodes coordinates to Australian state code
   * @param {number} lat - Latitude
   * @param {number} lng - Longitude
   * @returns {Promise<string|null>} State code or null
   */
  async getStateFromCoordinates(lat, lng) {
    const stateMapping = getStateNameMapping();

    try {
      const response = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`);
      const data = await response.json();
      
      if (data.principalSubdivision) {
        return stateMapping[data.principalSubdivision] || null;
      }
      return null;
    } catch (error) {
      console.error('Geocoding error:', error);
      return null;
    }
  }

  /**
   * Initializes UI event listeners and geolocation
   */
  init() {
    this.sectionElement = document.querySelector(`#franchise-opportunities-${this.sectionId}`);
    if (!this.sectionElement) return;
    
    const statePills = this.sectionElement.querySelectorAll('.state-tab.state-tab-desktop');
    const stateGroups = this.sectionElement.querySelectorAll('.state-group');
    if (!statePills?.length || !stateGroups?.length) return;

    // Desktop state tab handlers
    statePills.forEach((tab) => {
      if (!tab) return;
      const handler = (e) => {
        e.preventDefault();
        const selectedState = tab.getAttribute('data-state');
        if (!selectedState) return;
        statePills.forEach(p => {
          if (p) p.classList.remove('active');
        });
        tab.classList.add('active');
        this.showStateGroup(selectedState, stateGroups);
      };
      tab.addEventListener('click', handler);
      this.eventHandlers.push({ element: tab, type: 'click', handler });
    });

    // Mobile view controls
    const mobileViewToggles = this.sectionElement.querySelectorAll('.view-toggle');
    const mobileTabPanels = this.sectionElement.querySelectorAll('.mobile-tab-content .tab-panel');
    const mobileStatePills = this.sectionElement.querySelectorAll('.mobile-state-pills .state-tab');
    const mobileStateGroups = this.sectionElement.querySelectorAll('.mobile-layout .state-group');
    
    mobileViewToggles.forEach(toggle => {
      if (!toggle) return;
      const handler = (e) => {
        e.preventDefault();
        const view = toggle.getAttribute('data-view');
        if (!view) return;
        mobileViewToggles.forEach(t => {
          if (t) t.classList.remove('active');
        });
        toggle.classList.add('active');
        mobileTabPanels.forEach(panel => {
          if (!panel) return;
          const targetId = `mobile-${view}-view`;
          const isTarget = panel.id === targetId;
          panel.classList.toggle('active', isTarget);
          panel.classList.toggle('hidden', !isTarget);
        });
      };
      toggle.addEventListener('click', handler);
      this.eventHandlers.push({ element: toggle, type: 'click', handler });
    });

    // Viewport change handler with debouncing
    const handleViewportChange = () => {
      const isMobile = window.innerWidth <= 1023;
      const mobileLayout = this.sectionElement.querySelector('.mobile-layout');
      if (isMobile && mobileLayout) {
        const mobileLayoutStyle = window.getComputedStyle(mobileLayout);
        if (mobileLayoutStyle.display !== 'none') {
          const listToggle = this.sectionElement.querySelector('.view-toggle[data-view="list"]');
          const mapToggle = this.sectionElement.querySelector('.view-toggle[data-view="map"]');
          const listPanel = this.sectionElement.querySelector('#mobile-list-view');
          const mapPanel = this.sectionElement.querySelector('#mobile-map-view');
          if (listToggle && mapToggle && listPanel && mapPanel) {
            listToggle.classList.add('active');
            mapToggle.classList.remove('active');
            listPanel.classList.add('active');
            mapPanel.classList.remove('active');
          }
        }
      }
    };

    let resizeTimeout;
    this.resizeHandler = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(handleViewportChange, 100);
    };
    window.addEventListener('resize', this.resizeHandler);
    handleViewportChange();

    // Mobile state pills
    mobileStatePills.forEach(pill => {
      if (!pill) return;
      const handler = (e) => {
        e.preventDefault();
        const selectedState = pill.getAttribute('data-state');
        if (!selectedState) return;
        mobileStatePills.forEach(p => {
          if (p) p.classList.remove('active');
        });
        pill.classList.add('active');
        this.showStateGroup(selectedState, mobileStateGroups);
      };
      pill.addEventListener('click', handler);
      this.eventHandlers.push({ element: pill, type: 'click', handler });
    });

    // Geolocation handling
    const enableGeolocation = this.sectionElement.getAttribute('data-enable-geolocation') === 'true';
    
    if (enableGeolocation && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          const detectedState = await this.getStateFromCoordinates(latitude, longitude);
          
          if (detectedState && this.setActiveState(detectedState, statePills, stateGroups, mobileStatePills, mobileStateGroups)) {
            // State set successfully
          } else {
            const vicPill = Array.from(statePills).find(pill => pill.getAttribute('data-state') === 'VIC');
            const vicGroup = Array.from(stateGroups).find(group => group.getAttribute('data-state') === 'VIC');
            
            if (vicPill && vicGroup) {
              this.setActiveState('VIC', statePills, stateGroups, mobileStatePills, mobileStateGroups);
            }
          }
        },
        (error) => {
          console.log('Geolocation denied or failed:', error);
          const vicPill = Array.from(statePills).find(pill => pill.getAttribute('data-state') === 'VIC');
          const vicGroup = Array.from(stateGroups).find(group => group.getAttribute('data-state') === 'VIC');
          
          if (vicPill && vicGroup) {
            this.setActiveState('VIC', statePills, stateGroups, mobileStatePills, mobileStateGroups);
          }
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 150000
        }
      );
    } else {
      const vicPill = Array.from(statePills).find(pill => pill.getAttribute('data-state') === 'VIC');
      const vicGroup = Array.from(stateGroups).find(group => group.getAttribute('data-state') === 'VIC');

      if (vicPill && vicGroup) {
        this.setActiveState('VIC', statePills, stateGroups, mobileStatePills, mobileStateGroups);
      }
    }
  }

  /**
   * Cleanup method to remove all event listeners
   * Call this when the section is removed from DOM to prevent memory leaks
   */
  destroy() {
    // Remove all event listeners
    this.eventHandlers.forEach(({ element, type, handler }) => {
      if (element) {
        element.removeEventListener(type, handler);
      }
    });
    this.eventHandlers = [];

    // Remove resize listener
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      this.resizeHandler = null;
    }

    // Clear references
    this.sectionElement = null;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const sections = document.querySelectorAll('[id^="franchise-opportunities-"]');
  sections.forEach(section => {
    const sectionId = section.id.replace('franchise-opportunities-', '');
    new FranchiseOpportunitiesUI(sectionId);
  });
});
